import { MessageId } from '@/models/Message';
import { StepId, StepProgress, StepRunState } from '@/models/StepProgress';
import { ThreadId } from '@/models/Thread';

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface StepProgressStoreState {
  stepIdsByThreadId: Record<ThreadId, StepId[]>;
  stepIdsByMessageId: Record<MessageId, StepId[]>;
  latestStepProgressByStepId: Record<StepId, StepProgress>;
}

export const initialState: StepProgressStoreState = {
  latestStepProgressByStepId: {},
  stepIdsByMessageId: {},
  stepIdsByThreadId: {},
};

/*************
 * reducers *
 *************/

const addStepProgressToStore = (
  state: StepProgressStoreState,
  stepProgress: StepProgress,
): StepProgressStoreState => {
  let { stepIdsByThreadId, stepIdsByMessageId, latestStepProgressByStepId } =
    state;
  const { stepId, messageId, threadId } = stepProgress;
  const existingStepProgress = latestStepProgressByStepId[stepId];

  // If the step has already succeeded, don't update it. Occasionally, steps that go from pending to success quickly
  // come in out of order and we don't want to overwrite a successful step with a pending one
  if (
    existingStepProgress &&
    existingStepProgress.runState === StepRunState.SUCCEEDED
  ) {
    return state;
  }

  latestStepProgressByStepId = {
    ...latestStepProgressByStepId,
    [stepProgress.stepId]: stepProgress,
  };

  const stepIdsForMessageId = stepIdsByMessageId[messageId] || [];
  if (!stepIdsForMessageId.includes(stepId)) {
    stepIdsByMessageId = {
      ...stepIdsByMessageId,
      [messageId]: [...stepIdsForMessageId, stepId],
    };
  }

  const stepIdsForThreadId = stepIdsByThreadId[threadId] || [];
  if (!stepIdsForThreadId.includes(stepId)) {
    stepIdsByThreadId = {
      ...stepIdsByThreadId,
      [threadId]: [...stepIdsForThreadId, stepId],
    };
  }

  const newState = {
    ...state,
    stepIdsByThreadId,
    stepIdsByMessageId,
    latestStepProgressByStepId,
  };
  return newState;
};

export const addStepProgressReducer = (
  state: StepProgressStoreState,
  action: PayloadAction<StepProgress>,
) => {
  const stepProgress = action.payload;
  const newState = addStepProgressToStore(state, stepProgress);
  return newState;
};

export const addStepProgressListReducer = (
  state: StepProgressStoreState,
  action: PayloadAction<StepProgress[]>,
) => {
  const stepProgressList = action.payload;
  let newState = state;
  for (const stepProgress of stepProgressList) {
    newState = addStepProgressToStore(newState, stepProgress);
  }
  return newState;
};

/*************
 * selectors *
 *************/

// MEMOIZATION NOTE: Step progress is an event, so the ID will change when the
// content changes.
export const mkMemoizedStepProgressString = (list: StepProgress[]) =>
  list.map((_) => _.id).join(',');

export const getStepProgressByStepIdSelector = (
  state: StepProgressStoreState,
  stepId: StepId,
) => state.latestStepProgressByStepId[stepId] ?? null;

export const getStepProgressListFromStateByThreadId = (
  state: StepProgressStoreState,
  threadId: ThreadId,
): StepProgress[] => {
  const stepIds = state.stepIdsByThreadId[threadId] ?? [];
  return stepIds
    .map((stepId) => state.latestStepProgressByStepId[stepId])
    .filter(Boolean);
};

const getStepProgressListByThreadIdSelector = createSelector(
  (state: StepProgressStoreState, _: ThreadId) => state,
  (state: StepProgressStoreState, threadId: ThreadId) =>
    mkMemoizedStepProgressString(
      getStepProgressListFromStateByThreadId(state, threadId),
    ),
  getStepProgressListFromStateByThreadId,
);

export const getStepProgressListFromStateByMessageId = (
  state: StepProgressStoreState,
  messageId: MessageId,
): StepProgress[] => {
  const stepIds = state.stepIdsByMessageId[messageId] ?? [];
  return stepIds
    .map((stepId) => state.latestStepProgressByStepId[stepId])
    .filter(Boolean);
};

const getStepProgressListByMessageIdSelector = createSelector(
  (state: StepProgressStoreState, _: MessageId) => state,
  (state: StepProgressStoreState, messageId: MessageId) =>
    mkMemoizedStepProgressString(
      getStepProgressListFromStateByMessageId(state, messageId),
    ),
  getStepProgressListFromStateByMessageId,
);

/*************
 * slice *
 *************/

export const stepProgressStateStore = createSlice({
  name: 'stepProgressStore',
  initialState,
  reducers: {
    addStepProgress: addStepProgressReducer,
    addStepProgressList: addStepProgressListReducer,
  },
  selectors: {
    getStepProgressByStepId: getStepProgressByStepIdSelector,
    getStepProgressListByThreadId: getStepProgressListByThreadIdSelector,
    getStepProgressListByMessageId: getStepProgressListByMessageIdSelector,
  },
});

export const {
  getStepProgressByStepId,
  getStepProgressListByThreadId,
  getStepProgressListByMessageId,
} = stepProgressStateStore.selectors;
export const { addStepProgress, addStepProgressList } =
  stepProgressStateStore.actions;
export const stepProgressStoreReducer = stepProgressStateStore.reducer;
